import { useMemo, useState } from 'react';
import { InputAdornment, OutlinedInput } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ChatConversationSummary } from '../../interfaces/ChatInterface';
import { CHAT_CONVERSATIONS_PER_PAGE } from '../../constants';
import CustomButton from '../reusable/CustomButton';
import CustomPagination from '../reusable/CustomPagination';
import Panel from '../reusable/Panel';
import { filterConversations, formatLastEdited } from './chatUtils';

interface ChatConversationListProps {
  conversations: ChatConversationSummary[];
  onNewChat: () => void;
  onSelect: (scope: string) => void;
}

const ChatConversationList = ({ conversations, onNewChat, onSelect }: ChatConversationListProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = useMemo(() => {
    const matches = filterConversations(conversations, searchInput);
    return [...matches].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [conversations, searchInput]);

  const totalPages = Math.ceil(filtered.length / CHAT_CONVERSATIONS_PER_PAGE);
  const safePage = Math.min(currentPage, Math.max(totalPages, 1));
  const pageStart = (safePage - 1) * CHAT_CONVERSATIONS_PER_PAGE;
  const pageItems = filtered.slice(pageStart, pageStart + CHAT_CONVERSATIONS_PER_PAGE);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setCurrentPage(1);
  };

  return (
    <Panel className="chat-conversation-list-panel">
      <div className="chat-conversation-list-header">
        <h2 className="chat-panel-title">Conversations</h2>
        <CustomButton variant="primary" buttonText="New chat" buttonAction={onNewChat} />
      </div>

      <OutlinedInput
        className="chat-conversation-search"
        value={searchInput}
        onChange={(event) => handleSearchChange(event.target.value)}
        size="small"
        fullWidth
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        }
      />

      {filtered.length === 0 ? (
        <p className="chat-conversation-list-empty">No conversations found.</p>
      ) : (
        <>
          <ul className="chat-conversation-items">
            {pageItems.map((conversation) => (
              <li key={conversation.scope}>
                <button type="button" className="chat-conversation-item" onClick={() => onSelect(conversation.scope)}>
                  <span className="chat-conversation-item-text">
                    <span className="chat-conversation-item-title">{conversation.title}</span>
                    <span className="chat-conversation-item-meta">
                      Last edited {formatLastEdited(conversation.updated_at)}
                    </span>
                  </span>
                  <ChevronRightIcon className="chat-conversation-item-chevron" fontSize="small" />
                </button>
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <CustomPagination
              currentPage={safePage}
              pages={totalPages}
              totalItems={filtered.length}
              pageSize={CHAT_CONVERSATIONS_PER_PAGE}
              handlePageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </Panel>
  );
};

export default ChatConversationList;
